import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { KEY_RECOIL } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { useUpdateEffect } from '@/shared/lib/customHooks';

import {
  selectDailyCount,
  selectIsEmptyData,
  selectIsLoadingHrChart,
  selectIsShowAfibEvent,
  selectIsShowArtifactEvent,
  selectIsShowOtherEvent,
  selectJumpDate,
  selectSelectedDateValue,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { BookmarkHrChartOptionsProps } from './bookmarkHrChartOptions';

const ENUM_EVENT_ACTION = {
  OTHER_EVENT: 'other_event',
  ARTIFACT_EVENT: 'artifact_event',
  AFIB_EVENT: 'afib_event',
};

/**
 * Drives the date navigation and rhythm-event toggles above the ECG bookmark HR chart. Binds tab-2 report-store state through selectors, derives per-day bookmark counts and MM/DD dropdown labels, keeps the date index in step with the selected strip and any pending jump date through effects, and rebuilds the shared date list whenever the daily counts change.
 *
 * @param props - Chart inputs, notably the study timezone offset used to bucket bookmarks into days.
 * @returns An object carrying dailyCount, isLoadingHrChart, isEmptyData, dateIndex, dropdownOptions, formatSelectedDate, the toggleDisplayRhythmEvent, onChangeDate and handleClickArrow handlers, and ENUM_EVENT_ACTION.
 */
const useBookmarkHrChartOptions = ({ timezoneOffset }: BookmarkHrChartOptionsProps) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;

  const dailyCount = useReportStore(selectDailyCount(keyRecoil));
  const setDailyCountAction = useReportStore((s) => s.actions.setDailyCount);
  /**
   * Writes the per-day bookmark counts behind the HR chart. Forwards the value to the store action with the tab-2 key already bound, so callers pass only the new counts.
   *
   * @param v - The new daily-count array, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setDailyCount = (v: Parameters<typeof setDailyCountAction>[1]) =>
    setDailyCountAction(keyRecoil, v);
  /**
   * Reads the day currently selected on the ECG bookmark tab. Applies the tab-2 selector to the report store's current snapshot, giving an on-demand read that does not subscribe the hook to changes.
   *
   * @returns The ISO date string of the selected day.
   */
  const getSelectedDateValue = () => selectSelectedDateValue(keyRecoil)(useReportStore.getState());
  const setSelectedDateValueAction = useReportStore((s) => s.actions.setSelectedDateValue);
  /**
   * Stores the day currently selected on the ECG bookmark tab. Forwards the value to the store action with the tab-2 key already bound, so callers pass only the new ISO date.
   *
   * @param v - The newly selected date value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedDateValue = (v: Parameters<typeof setSelectedDateValueAction>[1]) =>
    setSelectedDateValueAction(keyRecoil, v);
  const isShowArtifactEvent = useReportStore(selectIsShowArtifactEvent(keyRecoil));
  const setIsShowArtifactEventAction = useReportStore((s) => s.actions.setIsShowArtifactEvent);
  /**
   * Controls whether artifact events are drawn on the bookmark HR chart. Forwards the value to the store action with the tab-2 key already bound, so callers pass only the flag.
   *
   * @param v - The new artifact visibility flag, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsShowArtifactEvent = (v: Parameters<typeof setIsShowArtifactEventAction>[1]) =>
    setIsShowArtifactEventAction(keyRecoil, v);
  const isShowAfibEvent = useReportStore(selectIsShowAfibEvent(keyRecoil));
  const setIsShowAfibEventAction = useReportStore((s) => s.actions.setIsShowAfibEvent);
  /**
   * Controls whether AFib events are drawn on the bookmark HR chart. Forwards the value to the store action with the tab-2 key already bound, so callers pass only the flag.
   *
   * @param v - The new AFib visibility flag, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsShowAfibEvent = (v: Parameters<typeof setIsShowAfibEventAction>[1]) =>
    setIsShowAfibEventAction(keyRecoil, v);
  const isShowOtherEvent = useReportStore(selectIsShowOtherEvent(keyRecoil));
  const setIsShowOtherEventAction = useReportStore((s) => s.actions.setIsShowOtherEvent);
  /**
   * Controls whether the remaining rhythm events are drawn on the bookmark HR chart. Forwards the value to the store action with the tab-2 key already bound, so callers pass only the flag.
   *
   * @param v - The new other-event visibility flag, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsShowOtherEvent = (v: Parameters<typeof setIsShowOtherEventAction>[1]) =>
    setIsShowOtherEventAction(keyRecoil, v);
  const isLoadingHrChart = useReportStore(selectIsLoadingHrChart(keyRecoil));
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const jumpDate = useReportStore(selectJumpDate(keyRecoil));
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Sets the date the ECG viewer should jump to from the bookmark tab. Forwards the value to the store action with the tab-2 key already bound; passing undefined clears a pending jump.
   *
   * @param v - The jump target date, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Sets the time within the jump date that the ECG viewer should land on. Forwards the value to the store action with the tab-2 key already bound; passing undefined clears it.
   *
   * @param v - The new jump time, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Sets which portion of the day the ECG viewer lands on after a bookmark jump. Forwards the value to the store action with the tab-2 key already bound.
   *
   * @param v - The new jump portion, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const isEmptyData = useReportStore(selectIsEmptyData(keyRecoil));
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Flags whether the selected day has no ECG data behind the bookmark HR chart. Forwards the value to the store action with the tab-2 key already bound, so callers pass only the flag.
   *
   * @param v - The new empty-data flag, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const dateList = useReportStore((s) => s.dateList);
  const setDateList = useReportStore((s) => s.actions.setDateList);
  const ecgBookmarks = useReportStore((s) => s.ecgBookmarks);
  /**
   * Reads the study's unfiltered per-day summary rows that the bookmark HR chart lays its date axis out from. Pulls originalDailySummaries straight off the report store's current snapshot instead of subscribing, so callers see the latest value without re-rendering.
   *
   * @returns The study's original daily summaries.
   */
  const getOriginalDailySummaries = () => useReportStore.getState().originalDailySummaries;

  const [dateIndex, setDateIndex] = useState(0);

  const dropdownOptions = useMemo(
    () =>
      _.map(
        dailyCount,
        (x) =>
          `${convertToMomentJs(x.date, timezoneOffset).format('MM/DD')} ${x.count === -1 ? '(No data)' : `(${x.count})`}`,
      ),
    [dailyCount],
  );

  /**
   * Would toggle the artifact, AFib or other rhythm-event overlays on the bookmark HR chart. Its whole body is commented out, so a call currently changes none of the three visibility flags.
   *
   * @param name - Identifier of the overlay to toggle, one of the ENUM_EVENT_ACTION values.
   * @returns Nothing.
   */
  const toggleDisplayRhythmEvent = (name: string) => {
    // switch (name) {
    //   case ENUM_EVENT_ACTION.ARTIFACT_EVENT: {
    //     setIsShowArtifactEvent(!isShowArtifactEvent);
    //     break;
    //   }
    //   case ENUM_EVENT_ACTION.AFIB_EVENT: {
    //     setIsShowAfibEvent(!isShowAfibEvent);
    //     break;
    //   }
    //   case ENUM_EVENT_ACTION.OTHER_EVENT: {
    //     setIsShowOtherEvent(!isShowOtherEvent);
    //     break;
    //   }
    //   default: {
    //     break;
    //   }
    // }
  };

  /**
   * Recomputes how many ECG bookmarks fall on each day of the study. Buckets every bookmark's center timestamp into a day using the timezone offset, tallies it against the original daily summaries, and stores a per-day count forced to -1 for days the summary marks empty.
   *
   * @returns A promise resolving once the per-day bookmark counts are written to the store.
   */
  const handleGetBookmarksDailyCount = async () => {
    const originalDailySummaries = getOriginalDailySummaries();
    // originalDailySummariesState (Recoil, Task 7) is typed IDailySummary[], missing isEmpty - atom-type gap.
    const dateArr = _.map(originalDailySummaries, (x: any) => ({
      date: x.date,
      isEmpty: x.isEmpty,
    }));
    const dateArrCount = _.map(dateArr, (x) => 0);
    _.forEach(ecgBookmarks, (x) => {
      const startDate = convertToMomentJs(x.center, timezoneOffset).startOf('days').toISOString();
      const index = dateArr.findIndex((x) => x.date === startDate);
      if (index > -1) {
        dateArrCount[index] += 1;
      }
    });
    setDailyCount(
      _.map(dateArr, (x, index) => ({
        date: x.date,
        count: x.isEmpty ? -1 : dateArrCount[index],
      })),
    );
  };

  /**
   * Applies a day picked from the HR chart date dropdown. Matches the chosen label to its index and, only when the resulting ISO date differs from the stored selection, clears the pending jump date, time and portion, moves the date index and marks the day empty when its count is -1.
   *
   * @param name - Name of the dropdown field raising the change.
   * @param value - The selected dropdown label, matched against the formatted date options.
   * @returns Nothing.
   */
  const onChangeDate = (name: string, value: string) => {
    const index = _.findIndex(dropdownOptions, (x) => x === value);
    if (index > -1) {
      const selectedDateMoment = convertToMomentJs(dailyCount[index].date, timezoneOffset);
      const changedSelectedDateValue = selectedDateMoment.toISOString();
      if (changedSelectedDateValue !== getSelectedDateValue()) {
        setJumpDate(undefined);
        setJumpTime(undefined);
        setJumpPortion(0);
        setDateIndex(index);
        setSelectedDateValue(changedSelectedDateValue);
        setIsEmptyData(dailyCount[index].count === -1);
      }
    }
  };

  /**
   * Steps the bookmark HR chart one day forward or backward from the arrow buttons. Clears the pending jump date, time and portion, moves the date index, then stores the new day's date and marks it empty when its bookmark count is -1.
   *
   * @param isRight - True to step to the next day, false to step to the previous one.
   * @returns Nothing.
   */
  const handleClickArrow = (isRight: boolean) => {
    const newDateIndex = isRight ? dateIndex + 1 : dateIndex - 1;
    setJumpDate(undefined);
    setJumpTime(undefined);
    setJumpPortion(0);
    setDateIndex(newDateIndex);
    setSelectedDateValue(dailyCount[newDateIndex].date);
    setIsEmptyData(dailyCount[newDateIndex].count === -1);
  };

  useEffect(() => {
    handleGetBookmarksDailyCount();
  }, [ecgBookmarks]);

  useUpdateEffect(() => {
    if (!_.isEmpty(selectedStrip)) {
      const rhythmEventStripDateString = convertToMomentJs(
        selectedStrip.start,
        timezoneOffset,
      ).format('MM/DD');
      let index = _.findIndex(dateList, (x) => x.format('MM/DD') === rhythmEventStripDateString);
      if (index > -1) {
        //* Case event between 2 days, prevent reselect current day
        // selectedStripState (Recoil, Task 7) is typed IEvent | IBeat | IEcgBookmark, missing startIndex on some members - atom-type gap.
        if ((selectedStrip as any)?.startIndex < 0) {
          const diffDay = Math.ceil(Math.abs((selectedStrip as any).startIndex / 1440));
          index += diffDay;
        }
        setDateIndex(index);
        setSelectedDateValue(dateList[index].toISOString());
        setIsEmptyData(dailyCount[index].count === -1);
      }
    } else if (ecgBookmarks.length === 0 && dateList.length) {
      setDateIndex(0);
      setSelectedDateValue(dateList[0].toISOString());
      setIsEmptyData(dailyCount[0].count === -1);
    }
  }, [selectedStrip?.id, dateList, ecgBookmarks]);

  useUpdateEffect(() => {
    if (jumpDate) {
      const rhythmEventStripDateString = convertToMomentJs(jumpDate, timezoneOffset).format(
        'MM/DD',
      );
      const index = _.findIndex(dateList, (x) => x.format('MM/DD') === rhythmEventStripDateString);
      if (index > -1) {
        setDateIndex(index);
        setSelectedDateValue(dateList[index].toISOString());
      }
    }
  }, [jumpDate]);

  useEffect(() => {
    const dateListTmp = _.map(dailyCount, (x) => convertToMomentJs(x.date, timezoneOffset));
    setDateList(dateListTmp);
  }, [dailyCount]);

  const formatSelectedDate = dropdownOptions[dateIndex];

  return {
    dailyCount,
    isLoadingHrChart,
    isEmptyData,
    dateIndex,
    dropdownOptions,
    formatSelectedDate,
    toggleDisplayRhythmEvent,
    onChangeDate,
    handleClickArrow,
    ENUM_EVENT_ACTION,
  };
};

export default useBookmarkHrChartOptions;
